import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Accordion } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, Database, Sparkles, Download, Send,
  CheckCircle2, FileText, Layers, Users, Search, ExternalLink, Printer, Images,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { MemoryBookStatusBadge, RoadTo2027Badge, CopfApprovedBadge } from "@/components/MemoryBookStatusBadge";
import { MemoryBookSectionEditor } from "@/components/MemoryBookSectionEditor";
import { MemoryBookViewer } from "@/components/MemoryBookViewer";
import { MemoryBookMediaLibrary } from "@/components/MemoryBookMediaLibrary";
import { MemoryBookImagesPanel } from "@/components/MemoryBookImagesPanel";
import api from "@/lib/api";

const AI_REGENERATABLE = new Set(["edito", "palmares", "road2027"]);

export default function AdminMemoryBookEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [version, setVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(null);
  // V9.20 — corpus counters récupérés côté médiathèque (indépendants du snapshot)
  const [corpus, setCorpus] = useState({ medias: [], external_links: [], manual_facts: [] });

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/memory-books/requests/${id}`);
      setRequest(res.data.request);
      setSnapshot(res.data.snapshot);
      setVersion(res.data.version);
      setVersions(res.data.versions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger la demande");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  const handleBuildSnapshot = async () => {
    setBusy("snapshot");
    try {
      await api.post(`/admin/memory-books/requests/${id}/snapshot`);
      toast.success("Snapshot construit");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec snapshot");
    } finally { setBusy(null); }
  };

  // V11.21 — Découverte de sources via IA (DuckDuckGo restreint aux sites tahitiens)
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discovered, setDiscovered] = useState([]);
  const [discoverBusy, setDiscoverBusy] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState({});

  const handleDiscoverSources = async () => {
    setDiscoverBusy(true);
    setDiscovered([]);
    setSelectedUrls({});
    setDiscoverOpen(true);
    try {
      const res = await api.post(`/admin/memory-books/requests/${id}/discover-sources`, {
        max_results: 6,
      });
      const results = res.data?.results || [];
      setDiscovered(results);
      const preSelect = {};
      results.forEach((r) => { preSelect[r.url] = true; });
      setSelectedUrls(preSelect);
      if (results.length === 0) {
        toast.info("Aucune source trouvée sur les sites tahitiens autorisés");
      } else {
        toast.success(`${results.length} source(s) découverte(s) · ${res.data.from_cache ? "cache 24h" : "recherche DDG gratuite"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la recherche");
    } finally { setDiscoverBusy(false); }
  };

  const handleAdoptSelected = async () => {
    const urls = Object.entries(selectedUrls).filter(([, v]) => v).map(([u]) => u);
    if (urls.length === 0) { toast.info("Sélectionne au moins une source"); return; }
    setDiscoverBusy(true);
    try {
      const res = await api.post(`/admin/memory-books/requests/${id}/discover-sources`, {
        adopt_urls: urls, max_results: 1,
      });
      toast.success(`${res.data.adopted_count} source(s) ajoutée(s) au snapshot`);
      setDiscoverOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec adoption");
    } finally { setDiscoverBusy(false); }
  };


  const handleGenerate = async (opts = {}) => {
    setBusy("generate");
    try {
      const params = opts.force ? "?force=true" : "";
      await api.post(`/admin/memory-books/requests/${id}/generate${params}`);
      toast.success("Brouillon généré (IA appelée sur snapshot)");
      await refresh();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Échec génération";
      if (status === 422) {
        const forceIt = window.confirm(
          `${msg}\n\nVoulez-vous forcer la génération d'un livret minimaliste (identité uniquement) ?`
        );
        if (forceIt) {
          return handleGenerate({ force: true });
        }
      } else {
        toast.error(msg);
      }
    } finally { setBusy(null); }
  };

  const handleFinalize = async () => {
    if (!version) return;
    setBusy("finalize");
    try {
      await api.post(`/admin/memory-books/versions/${version.id}/finalize`);
      toast.success("Version finalisée");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec finalisation");
    } finally { setBusy(null); }
  };

  const handleSubmitCopf = async () => {
    if (!version) return;
    setBusy("copf");
    try {
      await api.post(`/admin/memory-books/versions/${version.id}/submit-copf-review`);
      toast.success("Version soumise au COPF");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec transmission COPF");
    } finally { setBusy(null); }
  };

  const handleDownloadPdf = async () => {
    if (!version) return;
    setBusy("pdf");
    try {
      const res = await api.get(`/admin/memory-books/versions/${version.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec téléchargement PDF");
    } finally { setBusy(null); }
  };

  const handleSectionUpdated = () => {
    // Refetch complet pour rester cohérent
    refresh();
  };

  const sortedSections = useMemo(() => {
    if (!version?.sections) return [];
    return [...version.sections].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [version]);

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement de la demande…
      </div>
    );
  }
  if (!request) return <div className="p-6">Demande introuvable.</div>;

  const stats = snapshot ? snapshot.key_figures || {} : null;
  const sourceIds = snapshot?.source_ids || {};
  const isFinalized = !!version?.finalized_at;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-memorybook-editor-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav("/admin/memoires")}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Retour
        </Button>
      </div>

      {/* Header */}
      <Card>
        <CardContent className="p-4 md:p-6 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl md:text-2xl font-display font-semibold">
                {request.entity_name_snapshot || "Demande"}
              </h1>
              <MemoryBookStatusBadge status={request.status} />
              {request.road_to_2027 && <RoadTo2027Badge />}
              {version?.badges?.copf_approved && <CopfApprovedBadge />}
            </div>
            <p className="text-xs font-mono text-muted-foreground">
              {request.memory_type} · {request.entity_type} ·
              {request.period_year ? ` année ${request.period_year}` : " période complète"} ·
              demandé par {request.requester_email || request.requester_user_id?.slice(0, 8)}
            </p>
            {request.context && (
              <p className="text-sm italic text-muted-foreground">« {request.context} »</p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!snapshot && (
              <Button onClick={handleBuildSnapshot} disabled={busy === "snapshot"} data-testid="snapshot-build-button">
                {busy === "snapshot" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                Construire le snapshot
              </Button>
            )}
            {snapshot && !isFinalized && (
              <Button
                variant="outline"
                onClick={handleDiscoverSources}
                disabled={discoverBusy}
                data-testid="memorybook-discover-sources-button"
                title="Rechercher des sources sur tahiti-infos, tntv, polynésie 1ère, radio1, sport-tahiti"
              >
                {discoverBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Découvrir des sources IA
              </Button>
            )}
            {snapshot && !version && (
              <Button onClick={handleGenerate} disabled={busy === "generate"} data-testid="memorybook-generate-button">
                {busy === "generate" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Générer sections IA
              </Button>
            )}
            {snapshot && version && !isFinalized && (
              <Button variant="outline" onClick={handleGenerate} disabled={busy === "generate"} data-testid="memorybook-regenerate-all-button">
                {busy === "generate" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Nouvelle version
              </Button>
            )}
            {version && !isFinalized && (
              <Button onClick={handleFinalize} disabled={busy === "finalize"} data-testid="memorybook-finalize-button">
                {busy === "finalize" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Finaliser
              </Button>
            )}
            {version && (
              <Link to={`/admin/memoires/${id}/print`} target="_blank" rel="noopener">
                <Button variant="outline" data-testid="memorybook-preview-pdf-button">
                  <Printer className="h-4 w-4 mr-2" />
                  Aperçu PDF
                </Button>
              </Link>
            )}
            {version && isFinalized && (
              <Button variant="outline" onClick={handleDownloadPdf} disabled={busy === "pdf"} data-testid="memorybook-download-pdf-button">
                {busy === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Télécharger PDF
              </Button>
            )}
            {version && isFinalized && request.request_copf_approval && !version.submitted_copf_at && (
              <Button variant="secondary" onClick={handleSubmitCopf} disabled={busy === "copf"} data-testid="memorybook-submit-copf-button">
                {busy === "copf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Soumettre au COPF
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Layout 8/4 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          {!version ? (
            <Card>
              <CardContent className="p-6">
                {!snapshot ? (
                  <div className="space-y-3 text-sm">
                    <h2 className="font-medium">Étape 1 — Consolider les données</h2>
                    <p className="text-muted-foreground">
                      Aucun snapshot construit pour cette demande. Cliquez sur « Construire le snapshot »
                      pour figer les données AITO de la période.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Vous pourrez ensuite ajouter des <strong>sources externes</strong>, des <strong>faits manuels sourcés</strong>
                      et des <strong>images validées</strong> avant de lancer l'IA.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 text-sm">
                    <h2 className="font-medium">Étape 2 — Enrichir puis générer</h2>
                    <p className="text-muted-foreground">
                      Snapshot prêt. Complétez si nécessaire avec des sources externes ou faits sourcés,
                      puis lancez la génération. L'IA rédige STRICTEMENT à partir du corpus fourni.
                    </p>
                    <div className="rounded-lg border bg-secondary/40 p-3">
                      <div className="text-xs font-medium mb-2">Corpus disponible</div>
                      <div className="grid grid-cols-2 gap-y-1 text-xs">
                        <span className="text-muted-foreground">Palmarès (DB)</span>
                        <span className="font-mono text-right">{stats?.achievements_count ?? 0}</span>
                        <span className="text-muted-foreground">Compétitions (DB)</span>
                        <span className="font-mono text-right">{stats?.competitions_count ?? 0}</span>
                        <span className="text-muted-foreground">Articles (DB)</span>
                        <span className="font-mono text-right">{stats?.articles_count ?? 0}</span>
                        <span className="text-muted-foreground">Sources externes (manuel)</span>
                        <span className="font-mono text-right" data-testid="corpus-ext-links">{corpus.external_links?.length ?? 0}</span>
                        <span className="text-muted-foreground">Faits manuels (sourcés)</span>
                        <span className="font-mono text-right" data-testid="corpus-manual-facts">{corpus.manual_facts?.length ?? 0}</span>
                        <span className="text-muted-foreground">Médias validés</span>
                        <span className="font-mono text-right" data-testid="corpus-medias-validated">
                          {corpus.medias?.filter((m) => ["AUTHORIZED", "LICENSED", "INTERNAL"].includes(m.rights_status)).length ?? 0}
                        </span>
                      </div>
                    </div>
                    <MemoryBookMediaLibrary
                      requestId={id}
                      editable={true}
                      onCorpusChanged={setCorpus}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Tabs defaultValue="editor" className="w-full">
              <TabsList>
                <TabsTrigger value="editor" data-testid="memorybook-tab-editor">
                  <Layers className="h-4 w-4 mr-2" /> Sections
                </TabsTrigger>
                <TabsTrigger value="images" data-testid="memorybook-tab-images">
                  <Images className="h-4 w-4 mr-2" /> Images
                  {(() => {
                    const nToVerify = (corpus.medias || []).filter((m) => ["TO_VERIFY", "UNKNOWN"].includes(m.rights_status)).length;
                    return nToVerify > 0 ? (
                      <Badge className="ml-2 h-5 min-w-5 px-1.5 text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="memorybook-tab-images-count">
                        {nToVerify}
                      </Badge>
                    ) : null;
                  })()}
                </TabsTrigger>
                <TabsTrigger value="library" data-testid="memorybook-tab-library">
                  <Users className="h-4 w-4 mr-2" /> Médiathèque & sources
                </TabsTrigger>
                <TabsTrigger value="preview" data-testid="memorybook-tab-preview">
                  <FileText className="h-4 w-4 mr-2" /> Aperçu
                </TabsTrigger>
              </TabsList>
              <TabsContent value="editor" className="mt-4">
                <Accordion type="multiple" className="space-y-2">
                  {sortedSections.map((sec) => (
                    <MemoryBookSectionEditor
                      key={sec.key}
                      section={sec}
                      versionId={version.id}
                      snapshot={snapshot}
                      canRegenerate={AI_REGENERATABLE.has(sec.key)}
                      onSectionUpdated={handleSectionUpdated}
                    />
                  ))}
                </Accordion>
              </TabsContent>
              <TabsContent value="images" className="mt-4">
                <MemoryBookImagesPanel
                  requestId={id}
                  editable={!isFinalized}
                  onCorpusChanged={(items) => setCorpus((c) => ({ ...c, medias: items }))}
                />
              </TabsContent>
              <TabsContent value="library" className="mt-4">
                <MemoryBookMediaLibrary
                  requestId={id}
                  editable={!isFinalized}
                  onCorpusChanged={setCorpus}
                />
              </TabsContent>
              <TabsContent value="preview" className="mt-4">
                <MemoryBookViewer version={version} snapshot={snapshot} />
              </TabsContent>
            </Tabs>
          )}
        </div>

        <div className="lg:col-span-4 space-y-4 lg:sticky lg:top-20 self-start">
          <Card data-testid="memorybook-snapshot-stats">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Database className="h-4 w-4" /> Snapshot
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              {!snapshot ? (
                <p className="text-muted-foreground text-xs italic">Non construit.</p>
              ) : (
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-muted-foreground">Compétitions</span>
                  <span className="font-mono text-right" data-testid="snapshot-counts-competitions">{stats.competitions_count ?? 0}</span>
                  <span className="text-muted-foreground">Palmarès</span>
                  <span className="font-mono text-right" data-testid="snapshot-counts-achievements">{stats.achievements_count ?? 0}</span>
                  <span className="text-muted-foreground">Médailles</span>
                  <span className="font-mono text-right">{stats.medals_count ?? 0}</span>
                  <span className="text-muted-foreground">Titres Or</span>
                  <span className="font-mono text-right">{stats.golds_count ?? 0}</span>
                  <span className="text-muted-foreground">Articles</span>
                  <span className="font-mono text-right" data-testid="snapshot-counts-articles">{stats.articles_count ?? 0}</span>
                  <span className="text-muted-foreground">International</span>
                  <span className="font-mono text-right">{stats.international_count ?? 0}</span>
                </div>
              )}
              {snapshot && (
                <div className="pt-3 border-t mt-2 text-[10px] font-mono text-muted-foreground">
                  ID {snapshot.id?.slice(0, 8)} · généré le {new Date(snapshot.created_at).toLocaleString("fr-FR")}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4" /> Versions
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-2">
              {versions.length === 0 && <p className="text-muted-foreground italic">Aucune version créée.</p>}
              {versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between">
                  <span className="font-mono">v{v.version_number}</span>
                  <MemoryBookStatusBadge status={v.status} />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* V11.21 — Dialog "Découvrir des sources IA" */}
      <Dialog open={discoverOpen} onOpenChange={setDiscoverOpen}>
        <DialogContent className="max-w-2xl" data-testid="discover-sources-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" /> Sources trouvées sur les sites tahitiens
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {discoverBusy && discovered.length === 0 && (
              <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Recherche en cours…
              </div>
            )}
            {!discoverBusy && discovered.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">
                Aucune source pertinente trouvée.
              </div>
            )}
            {discovered.map((r) => (
              <label
                key={r.url}
                className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/40 cursor-pointer"
                data-testid={`discover-source-item`}
              >
                <Checkbox
                  checked={!!selectedUrls[r.url]}
                  onCheckedChange={(v) => setSelectedUrls((s) => ({ ...s, [r.url]: !!v }))}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{r.source_domain}</Badge>
                    <div className="font-medium text-sm truncate">{r.title || "(sans titre)"}</div>
                  </div>
                  {r.snippet && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.snippet}</p>
                  )}
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[11px] text-[hsl(var(--aito-lagoon))] hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    {r.url.slice(0, 80)}… <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscoverOpen(false)}>Fermer</Button>
            <Button
              onClick={handleAdoptSelected}
              disabled={discoverBusy || Object.values(selectedUrls).every((v) => !v)}
              data-testid="discover-adopt-selected"
              className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
            >
              {discoverBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Ajouter au livret
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
